import { Loader2, X } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input, Textarea } from "@/components/ui/input";
import { api } from "@/lib/api";
import type { ApplicationStatus, TrackedApplication } from "@/lib/types";

const STATUSES: ApplicationStatus[] = [
  "applied",
  "interested",
  "screening",
  "interview",
  "offer",
  "rejected",
  "withdrawn",
];

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Add an application made somewhere else - a careers page, an email, a fair.
 *
 * Only the role and the company are needed. Pasting the posting text is
 * optional, but with it "Should I apply?", the study pack and interview
 * practice work for this row as for any posting PRAXA found itself. Saving
 * costs nothing: no model is called.
 */
export function AddApplication({
  onAdded,
  onClose,
}: {
  onAdded: (row: TrackedApplication) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState("");
  const [company, setCompany] = useState("");
  const [location, setLocation] = useState("");
  const [url, setUrl] = useState("");
  const [status, setStatus] = useState<ApplicationStatus>("applied");
  const [appliedOn, setAppliedOn] = useState(today);
  const [description, setDescription] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ready = title.trim() !== "" && company.trim() !== "" && !saving;

  async function submit() {
    if (!ready) return;
    setSaving(true);
    setError(null);
    try {
      const row = await api.applications.add({
        title: title.trim(),
        company: company.trim(),
        ...(location.trim() ? { location: location.trim() } : {}),
        ...(url.trim() ? { url: url.trim() } : {}),
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        status,
        ...(appliedOn && status !== "interested" ? { appliedOn } : {}),
      });
      onAdded(row);
    } catch (reason) {
      setError((reason as Error).message);
      setSaving(false);
    }
  }

  return (
    <section className="space-y-4 rounded-2xl border bg-card/50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold">Add an application</h2>
          <p className="text-xs text-muted-foreground">
            For one you made somewhere else. Role and company are enough; paste
            the posting too and &ldquo;Should I apply?&rdquo;, the study pack
            and interview practice work for it.
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label="Close"
        >
          <X aria-hidden />
        </Button>
      </div>

      <form
        className="grid gap-3 sm:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <label className="space-y-1 text-xs text-muted-foreground">
          Role *
          <Input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="DevOps Engineer"
            required
          />
        </label>
        <label className="space-y-1 text-xs text-muted-foreground">
          Company *
          <Input
            value={company}
            onChange={(event) => setCompany(event.target.value)}
            placeholder="Bosch"
            required
          />
        </label>
        <label className="space-y-1 text-xs text-muted-foreground">
          Location
          <Input
            value={location}
            onChange={(event) => setLocation(event.target.value)}
            placeholder="Stuttgart, or Remote"
          />
        </label>
        <label className="space-y-1 text-xs text-muted-foreground">
          Link to the posting
          <Input
            type="url"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="https://"
          />
        </label>
        <label className="space-y-1 text-xs text-muted-foreground">
          Status
          <select
            className="flex h-9 w-full rounded-md border bg-transparent px-2 text-sm text-foreground"
            value={status}
            onChange={(event) =>
              setStatus(event.target.value as ApplicationStatus)
            }
          >
            {STATUSES.map((entry) => (
              <option key={entry} value={entry}>
                {entry}
              </option>
            ))}
          </select>
        </label>
        <label className="space-y-1 text-xs text-muted-foreground">
          Applied on
          <Input
            type="date"
            value={appliedOn}
            onChange={(event) => setAppliedOn(event.target.value)}
            disabled={status === "interested"}
          />
        </label>
        <label className="space-y-1 text-xs text-muted-foreground sm:col-span-2">
          Posting text (optional)
          <Textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Paste the job description here"
            className="min-h-[96px]"
          />
        </label>
        <label className="space-y-1 text-xs text-muted-foreground sm:col-span-2">
          Note
          <Input
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Referred by Anna; applied through their careers page"
          />
        </label>

        {error ? (
          <p className="text-sm text-destructive sm:col-span-2">{error}</p>
        ) : null}

        <div className="flex gap-2 sm:col-span-2">
          <Button type="submit" size="sm" disabled={!ready}>
            {saving ? (
              <Loader2 className="size-3.5 animate-spin" aria-hidden />
            ) : null}
            Add to my applications
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </section>
  );
}
